<template>
	<ClientOnly>
		<CardGeneric
			v-if="isRefundableBookingEnabled"
			class="text-left text-primary refundableBookingComponent">
			<div class="cardTitle relative mb-2">
				<p class="inline">{{ $t("refundableBookingCardTitle") }}</p>
				<IconsFontAwesome
					icon="fa-solid fa-circle-question"
					class="cursor-pointer hover:text-secondary hoverEffects text-sm absolute top-[0.2em] ml-1"
					@click="openInfoPopup" />
			</div>

			<CheckoutRefundableBookingPopup ref="refundableBookingInfoModal" />

			<div
				role="radiogroup"
				class="flex flex-col gap-4">
				<template v-if="hasRefundableItems && hasMinimumRefundableItems">
					<CardSecondary
						class="!p-2 !rounded-md cursor-pointer"
						@click="changeRefundableBooking(true)">
						<div class="flex justify-between">
							<div class="flex">
								<FormRadio
									id="refundablebooking"
									:checked="isRefundableBookingSelected" />
								<div class="ml-2 font-thin">
									<span class="text-sm">&nbsp;{{ $t("refundable") }}</span>
									<span class="text-xxs">&nbsp;{{ $t("recommended") }}</span>
								</div>
							</div>

							<p class="font-serif self-center">
								<span v-if="refundableBookingCost > 0"
									>{{ currency.symbol }}{{ parseFloat(refundableBookingCost).toFixed(2) }}</span
								>
								<span v-else>{{ $t("ellipsis") }}</span>
							</p>
						</div>
					</CardSecondary>

					<CardSecondary
						class="!p-2 !rounded-md cursor-pointer"
						@click="changeRefundableBooking(false)">
						<div class="flex justify-start">
							<div class="flex">
								<FormRadio
									id="nonRefundablebooking"
									:checked="!isRefundableBookingSelected" />
								<div class="ml-2 font-thin">
									<p class="text-sm">&nbsp;{{ $t("nonRefundable") }}</p>
								</div>
							</div>
						</div>
					</CardSecondary>

					<div v-if="isRefundableBookingSelected && hasRefundableItems">
						<p class="text-ternary text-xs">{{ $t("refundableItems") }}</p>

						<ul class="list-disc mt-1 pl-5 text-primary font-serif">
							<li
								v-for="item in refundableItems"
								:key="item.quantity + item.title">
								({{ item.quantity }}) {{ item.title }}
							</li>
						</ul>
					</div>
				</template>

				<template v-else-if="hasRefundableItems && !hasMinimumRefundableItems">
					<CardSecondary class="!p-2 !rounded-md">
						<p class="font-thin text-sm">{{ $t("noRefundableMinimum") }}</p>
					</CardSecondary>
				</template>

				<template v-else>
					<CardSecondary class="!p-2 !rounded-md">
						<p class="font-thin text-sm">{{ $t("noRefundableItems") }}</p>
					</CardSecondary>
				</template>
			</div>
		</CardGeneric>
	</ClientOnly>
</template>

<script setup>
	import { useCartStore } from "~/stores/CartStore"
	import { storeToRefs } from "pinia"
	import { useCurrencyStore } from "~/stores/CurrencyStore"
	import { useNuxtApp } from "#app"

	const runtimeConfig = useRuntimeConfig()
	const refundableBookingInfoModal = ref(null)
	const cartStore = useCartStore()
	const { refundableBookingCost, isRefundableBookingSelected } = storeToRefs(cartStore)

	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)

	const openInfoPopup = () => refundableBookingInfoModal.value.openModal()

	const { $cartService, $configs } = useNuxtApp()
	const changeRefundableBooking = (value) => {
		$cartService.$updateRefundableBooking(value)
	}

	const refundableItems = computed(() => {
		const allCartItems = [...cartStore.items.values()]
		return allCartItems.filter((item) => item.isInsurable)
	})

	const hasRefundableItems = computed(() => {
		return refundableItems.value.length > 0
	})

	//Business logic to limit refundable booking to be a minimum fee of $19
	const hasMinimumRefundableItems = computed(() => {
		const allRefundableItems = refundableItems.value
		const refundableItemsSubtotalCents = allRefundableItems.reduce(
			(acc, item) => acc + Math.round(item.subtotal * 100),
			0
		)
		return refundableItemsSubtotalCents >= 1900
	})

	const refundableBookingConfig = runtimeConfig.public.ENABLE_REFUNDABLE_BOOKING
	const isRefundableBookingEnabled = $configs.$isConfigEnabled(refundableBookingConfig)
</script>

<style lang="scss" scoped></style>
